import React, { useState, useEffect } from 'react';
import Text from 'components/Text';
import Heading from 'components/Heading';
import MediaObject from 'components/MediaObject';
import LinkBase from 'components/LinkBase';
import Glyph from 'components/Glyph';
import type { DeliverySummariesFromStream_me_tempStreamItem_MessageItem_reactionSummary } from 'chat/queries/__generated__/DeliverySummariesFromStream';

import { organizeData } from './organizeData';
import emojiAnnotationMap from 'emoji-annotation-to-unicode';

type Props = {
  reactionSummary: DeliverySummariesFromStream_me_tempStreamItem_MessageItem_reactionSummary;
};

const emojiChar = (reaction: string) =>
  String.fromCodePoint(Number('0x' + emojiAnnotationMap[reaction]));

const message = (numPeople: number, reactionIcons: string[]) => {
  const reactionIconsHex = reactionIcons.map(emojiChar);
  const lastReaction = reactionIconsHex.pop();
  const reactions =
    reactionIconsHex.length > 0
      ? reactionIconsHex.join(', ') + ' and ' + lastReaction
      : lastReaction;

  return `${numPeople} ${numPeople > 1 ? 'people' : 'person'} reacted with ${reactions}`;
};

const useScrollWhenOpenedEffect = (topRef: React.RefObject<HTMLDivElement>, isOpen: boolean) => {
  useEffect(() => {
    if (topRef && isOpen) {
      topRef?.current?.scrollIntoView(true);
    }
  }, [topRef, isOpen]);
};

const INITIAL_OPEN_STATE = false;

const ReactionsSection = ({ reactionSummary }: Props) => {
  const topRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(INITIAL_OPEN_STATE);
  const { numPeople, numReactions, reactions, usersAndReactions } = organizeData(
    reactionSummary.reactions
  );
  useScrollWhenOpenedEffect(topRef, isOpen);
  const subtext = message(numPeople, reactions);
  const toggle = () => (isOpen ? setOpen(false) : setOpen(true));

  return (
    <div className="DeliveryModalSection">
      <LinkBase className="DeliveryModalSection-header" onClick={toggle}>
        <div>
          <div ref={topRef} />
          <div>
            <Heading level="4">
              <MediaObject gutter="xsmall" center>
                <Text>Reactions</Text>
                {isOpen ? (
                  <Glyph
                    data-test="fold-icon"
                    data-testid="reactions-fold-icon"
                    solo
                    size="small"
                    color="mascot"
                    name="chevron-up"
                  />
                ) : (
                  <Glyph
                    data-test="fold-icon"
                    data-testid="reactions-fold-icon"
                    solo
                    size="small"
                    color="mascot"
                    name="chevron-down"
                  />
                )}
              </MediaObject>
            </Heading>
          </div>
          <div>
            <Text color="graphite" size="xxsmall">
              {subtext}
            </Text>
          </div>
        </div>

        <div>
          <Text data-testid="reactions-count" size="xlarge">
            {numReactions}
          </Text>
        </div>
      </LinkBase>
      {isOpen && (
        <div className="DeliveryModalSection-fold">
          {Object.keys(usersAndReactions).map((key) => (
            <div key={key} className="DeliveryModalSection-user">
              <div className="DeliveryModalSection-user-name" data-testid="reaction-user-name">
                {usersAndReactions[key].user.name}
              </div>
              <div
                className="DeliveryModalSection-user-reactions"
                data-testid="reaction-user-reactions"
              >
                {usersAndReactions[key].reactions.map(emojiChar).join(' ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReactionsSection;
